/** Rule identifiers are stable API: the UI links to them and docs cite them. */
export const RULE_IDS = [
  'MTH-001',
  'MTH-002',
  'MTH-003',
  'MTH-004',
  'MTH-005',
  'MTH-006',
  'MTH-007',
  'MTH-008',
  'MTH-009',
  'MTH-010',
  'MTH-011',
  'MTH-012',
  'MTH-013',
  'MTH-014',
  'MTH-020',
  'MTH-021',
  'MTH-022',
  'MTH-023',
  'MTH-024',
  'MTH-025',
  'MTH-030',
  'MTH-031',
  'MTH-032',
  'MTH-034',
] as const;

export type RuleId = (typeof RULE_IDS)[number];

export type Severity = 'error' | 'warning';

export interface Diagnostic {
  readonly ruleId: RuleId;
  readonly severity: Severity;
  readonly message: string;
  readonly nodeId?: string;
  readonly property?: string;
  /** Actionable next step, rendered verbatim in the validation table. */
  readonly fix?: string;
}

/** Rule metadata for the validation table; carries no logic. */
export interface Rule {
  readonly id: RuleId;
  readonly title: string;
  readonly description: string;
  readonly severity: Severity;
}

export interface ValidationReport {
  readonly diagnostics: readonly Diagnostic[];
  readonly errorCount: number;
  readonly warningCount: number;
  /** True when there is no diagnostic of severity `error`. */
  readonly valid: boolean;
}

export interface ValidateOptions {
  /**
   * Method `@id`s known to exist outside this document. Supplying it closes the
   * world: an unresolved `variantOf` becomes an error (MTH-005) instead of a
   * warning (MTH-006). Publishing supplies it; standalone file import does not.
   */
  readonly knownMethodIds?: readonly string[];
  /** Rules to skip, e.g. while a draft is still being assembled. */
  readonly disabledRules?: readonly RuleId[];
}

export function formatDiagnostic(diagnostic: Diagnostic): string {
  const where = diagnostic.nodeId === undefined ? '' : ` [${diagnostic.nodeId}`;
  const property = diagnostic.property === undefined ? '' : `.${diagnostic.property}`;
  const close = diagnostic.nodeId === undefined ? '' : ']';
  return `${diagnostic.ruleId} ${diagnostic.severity}${where}${property}${close}: ${diagnostic.message}`;
}
